<template>
    <div class="p-4 bg-white shadow-md">
        <h2 class="text-xl font-semibold text-gray-800 flex items-center mb-4">
            <span class="text-2xl mr-2">🎓</span>
            <span>Graduation Status By Program</span>
        </h2>

        <div v-if="hasData" class="flex flex-col">
            <apexchart type="bar" height="500" :series="series" :options="chartOptions" />

            <!-- Responsive table -->
            <div class="overflow-x-auto">
                <table class="min-w-full border border-gray-200 divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th class="border border-gray-200 px-4 py-2 text-left text-gray-700">Program</th>
                            <th class="border border-gray-200 px-4 py-2 text-center text-gray-700">On-time Graduation
                            </th>
                            <th class="border border-gray-200 px-4 py-2 text-center text-gray-700">Delayed Graduation
                            </th>
                            <th class="border border-gray-200 px-4 py-2 text-center text-gray-700">On-time %</th>
                            <th class="border border-gray-200 px-4 py-2 text-center text-gray-700">Delayed %</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200">
                        <tr v-for="program in data" :key="program.label" class="hover:bg-gray-50">
                            <td class="border border-gray-200 px-4 py-2">{{ program.label }}</td>
                            <td class="border border-gray-200 px-4 py-2 text-center">{{ program.on_time_graduation || 0
                            }}</td>
                            <td class="border border-gray-200 px-4 py-2 text-center">{{ program.delayed_graduation || 0
                            }}</td>
                            <td class="border border-gray-200 px-4 py-2 text-center">{{ program.on_time_percentage !=
                                null ? program.on_time_percentage.toFixed(2) + '%' : 'N/A' }}</td>
                            <td class="border border-gray-200 px-4 py-2 text-center">{{ program.delayed_percentage !=
                                null ? program.delayed_percentage.toFixed(2) + '%' : 'N/A' }}</td>
                        </tr>
                    </tbody>
                    <tfoot class="bg-gray-100 font-semibold">
                        <tr>
                            <td class="border border-gray-200 px-4 py-2 text-left">Total</td>
                            <td class="border border-gray-200 px-4 py-2 text-center">{{ totalOnTime }}</td>
                            <td class="border border-gray-200 px-4 py-2 text-center">{{ totalDelayed }}</td>
                            <td class="border border-gray-200 px-4 py-2 text-center">{{
                                overallOnTimePercentage.toFixed(2) }}%</td>
                            <td class="border border-gray-200 px-4 py-2 text-center">{{
                                overallDelayedPercentage.toFixed(2) }}%</td>
                        </tr>
                    </tfoot>
                </table>
            </div>


        </div>
        <div v-else class="text-gray-500 text-center py-16">
            <div class="text-4xl mb-2">⚠️</div>
            <div>There is no data to show</div>
        </div>
    </div>
</template>

<script>
export default {
    name: "GraduationStatusByProgram",
    props: {
        data: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        hasData() {
            return Array.isArray(this.data) && this.data.length > 0;
        },
        series() {
            const safe = (v) => (v == null ? 0 : v);
            return [
                {
                    name: "On-time Graduation",
                    data: this.data.map((d) => safe(d.on_time_graduation)),
                },
                {
                    name: "Delayed Graduation",
                    data: this.data.map((d) => safe(d.delayed_graduation)),
                },
            ];
        },
        chartOptions() {
            return {
                chart: {
                    type: "bar",
                    stacked: true,
                    height: 500,
                    toolbar: { show: false },
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: "60%",
                        dataLabels: {
                            total: {
                                enabled: true,
                                offsetX: 0,
                                style: { fontSize: "12px", fontWeight: 700 },
                            },
                        },
                    },
                },
                dataLabels: {
                    enabled: true,
                    formatter: (val) => (val ? val : "0"),
                    style: { fontSize: "11px", fontWeight: 600 },
                },
                xaxis: {
                    categories: this.data.map((d) => d.label),
                    title: { text: "Number of Students" },
                },
                yaxis: { title: { text: undefined } },
                tooltip: {
                    y: {
                        formatter: (val) => `${val} students`,
                    },
                },
                legend: {
                    position: "top",
                    horizontalAlign: "right",
                    offsetX: 40,
                },
                colors: ["#4C78A8", "#F58518"],
                fill: {
                    type: 'gradient',
                    gradient: {
                        shade: 'light',
                        type: 'vertical',
                        shadeIntensity: 0.5,
                        gradientToColors: ['#86B7E0', '#FDC68B'],
                        inverseColors: false,
                        opacityFrom: 0.9,
                        opacityTo: 0.9,
                        stops: [0, 100]
                    }
                },
            };
        },
        totalOnTime() {
            return this.data.reduce((sum, d) => sum + (d.on_time_graduation || 0), 0);
        },
        totalDelayed() {
            return this.data.reduce((sum, d) => sum + (d.delayed_graduation || 0), 0);
        },
        overallOnTimePercentage() {
            const total = this.totalOnTime + this.totalDelayed;
            return total ? (this.totalOnTime / total) * 100 : 0;
        },
        overallDelayedPercentage() {
            const total = this.totalOnTime + this.totalDelayed;
            return total ? (this.totalDelayed / total) * 100 : 0;
        },
    },
};
</script>